import React, { useEffect, useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import isFunction from 'lodash/isFunction';
import union from 'lodash/union';
import { ButtonWithIcon } from '@els/els-react--button';
import { Card } from '@els/els-react--card';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';
import ShowMore from '@Components/Results/utils/ShowMore';
import Reaction from '@Components/Results/reactions/Reaction';
import * as ResultsDataManage from '@Services/ResultsDataManage';
import Breadcrumb from '@Services/Breadcrumb';
import { enrichParamsWithSemanticFlags } from '@Pages/Results/semanticSearchUtils';

import './DocumentReactions.scss';

function DocumentReactions({
  metadata,
  documentIndex,
  documentRelativeIndex,
  databaseId,
  reactionsCount,
  isHit,
  closeClick,
  minCount = 3,
  maxCount = 40,
  supplierNumber,
  zoom,
}) {
  const [reactions, setReactions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const pendingRequestRef = useRef(null);

  const getReactionsForCitations = useCallback(() => {
    const currentCount = reactions.length;
    const context = 'reactions';
    const nextReactionsCount = currentCount === 0 ? minCount : maxCount;
    setIsLoading(true);

    const activeBreadcrumb = Breadcrumb.getBreadcrumbForDbByResultName(databaseId, metadata?.resultName);
    const paramWithSemantic = enrichParamsWithSemanticFlags(
      {
        resultName: metadata.resultName,
        resultIndex: documentIndex,
        resultRelativeIndex: documentRelativeIndex,
        countOfCurrentItems: currentCount,
        totalItems: reactionsCount,
        databaseId,
        sort: metadata.sort,
        order: metadata.order,
        context,
        count: nextReactionsCount,
        hitData: isHit,
      },
      activeBreadcrumb
    );

    const req = ResultsDataManage.getItemsForCitations(paramWithSemantic)
      .then((data) => {
        if (!data) {
          Logger.error('No results returned for document reactions');
          return;
        }
        // Avoid >4 levels of nesting by extracting mapping logic and using a for loop
        function mapReactions(dataArr) {
          const mapped = [];
          let i = 0;
          while (i < dataArr.length) {
            const reaction = dataArr[i];
            mapped.push({
              ...reaction,
              detailsCount: reaction.detailsCount || get(reaction, 'details.length'),
              index: String(i),
              databaseId,
              supplierNumber,
            });
            i += 1;
          }
          return mapped;
        }
        setReactions((prev) => union(prev, mapReactions(data)));
        setIsLoading(false);
      })
      .catch((e) => {
        Logger.error(e);
        setIsLoading(false);
      });
    pendingRequestRef.current = req;
    return req;
  }, [
    metadata,
    documentIndex,
    documentRelativeIndex,
    reactions.length,
    reactionsCount,
    databaseId,
    isHit,
    minCount,
    maxCount,
    supplierNumber,
  ]);

  const showLessItems = useCallback(() => {
    setReactions((prev) => prev.slice(0, minCount));
  }, [minCount]);

  useEffect(() => {
    getReactionsForCitations();
    return () => {
      const cancel = get(pendingRequestRef.current, 'cancel');
      if (isFunction(cancel)) {
        cancel();
      }
      pendingRequestRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClose = () => {
    const event = new CustomEvent('documentSectionClosed', {
      detail: { closedSection: isHit ? 'hitReactionsExpanded' : 'reactionsExpanded', focusNext: true },
    });
    document.dispatchEvent(event);
    closeClick();
  };

  const sectionId = isHit ? 'hitReactionsExpanded' : 'reactionsExpanded';

  return (
    <div
      className="result-detail-panel u-els-margin-top-1o2"
      data-section={sectionId}
      id={`${sectionId}-content`}
      tabIndex="-1"
      role="region"
      aria-labelledby={`${sectionId}-heading`}>
      <Card className="result-detail-panel-wrapper" id="reactions-wrapper">
        <ButtonWithIcon
          type="link"
          linkIconSize="s"
          linkOptions={['navigation']}
          data-e2e="e2e-reactions-close"
          className="els-btn-modal-close e2e-close"
          sprite="Close"
          onClick={() => {
            handleClose();
          }}
          iconOnly>
          Close Reactions
        </ButtonWithIcon>
        <h4 className="u-els-font-family-bold u-els-margin-bottom-1o2" id={`${sectionId}-heading`}>
          {isHit ? 'Hit Reactions' : 'Reactions'}
        </h4>
        <div className="reactions">
          {reactions.map((reaction, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <div className="rx-list-item" key={index}>
              <article className="rx-element rx-element--reaction">
                <Reaction
                  metadata={{ ...metadata, context: 'reactions', fromDocuments: true }}
                  reaction={reaction}
                  zoom={zoom}
                  spinnerId={`rx-element-${new Date().valueOf()}`}
                  databaseId={databaseId}
                  documentResults
                />
              </article>
            </div>
          ))}
        </div>
        <div
          className={`rx-btn-group--center ${reactions.length > minCount && reactionsCount !== minCount ? 'sticky-show-less-container' : ''}`}>
          {!metadata.isViewPreview && reactions.length > 0 && (
            <ShowMore
              idSuffix={`reactions-for-citations-${documentIndex}`}
              labelAll={
                reactionsCount - reactions.length < 50 ? `Show All Available ${reactionsCount - reactions.length}` : `Show next ${maxCount}`
              }
              initial={minCount}
              current={reactions.length}
              total={reactionsCount}
              container="#reactions-wrapper"
              moreHandler={getReactionsForCitations}
              lessHandler={showLessItems}
              documentResults
              isLoading={isLoading}
            />
          )}
        </div>
      </Card>
    </div>
  );
}

DocumentReactions.propTypes = {
  metadata: MetadataPropTypesShape,
  documentIndex: PropTypes.string.isRequired,
  documentRelativeIndex: PropTypes.string,
  databaseId: PropTypes.string.isRequired,
  reactionsCount: PropTypes.number.isRequired,
  isHit: PropTypes.bool,
  closeClick: PropTypes.func.isRequired,
  minCount: PropTypes.number,
  maxCount: PropTypes.number,
  supplierNumber: PropTypes.number,
  zoom: PropTypes.number,
};

export default DocumentReactions;
